import {
  errorsToPropertyErrors,
  flattenErrorTypeArray,
  Flex,
  FlexColumn,
  IconButton,
  SecondaryButton,
  Table,
  Tooltip,
  useDeleteAlert,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import { useMemo } from 'react';
import Section from '../../../components/Section';
import { StaticDataItem } from '../../../../../api/useAccountApi';
import { TableStatusIcon } from '../../../Icons/TableStatusIcon';
import { useAccountContext } from '../../../../../contexts';
import { DeleteIcon, PlusIcon } from '../../../components/Icons';
import {
  Applicant,
  EmploymentType,
} from '../../../../../contexts/account/Account';
import EmploymentHistoryExpandable from './EmploymentHistoryExpandable';
import { useToast } from '../../../../../hooks/useToast';
import useErrorBasedRowStyles from '../../../../../hooks/useErrorBasedRowStyles';
import { collectIdFieldValues } from '../../../utils/errors';
import { EmploymentHistory, EmploymentWithIncome, NotWorkingStatus } from '../../../../../contexts/account/IncomeAndEmployment';
import { ineSectionBodyCss, ineSectionHeaderCss, ineSectionTitleCss } from './IncomeAndEmploymentSection';
import { useProgressStatusByInitiator } from '../../../../../contexts/progressStatus/ProgressStatusProvider';

const AddPositionContainer = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[4]} 0;
  justify-content: end;
`}`;

const SectionHeader = styled(FlexColumn)`${({ theme }) => `
  padding: 0 ${theme.padding[4]};
`}`;

const AddPositionButton = styled(SecondaryButton)`
  width : 140px;
`;

const IconContainer = styled.div`${({ theme }) => `
  width: ${theme.padding[5]};
  height: ${theme.padding[5]};
  margin: auto;
`}`;

const DeleteIconButton = styled(DeleteIcon)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`}`;

const EmploymentStatusWrapper = styled.div`
  max-width: 100%;
`;

const EmploymentStatusText = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const errorContainerCss = css`${({ theme }) => `
  padding: 0 ${theme.padding[4]};
`}`;

function StatusIcon(employmentHistory: EmploymentHistory) {
  const { account } = useAccountContext();
  const { errors, warnings } = account ?? {};
  const { id } = employmentHistory;
  const applicantEmploymentHistoryErrors = [...collectIdFieldValues(employmentHistory).flatMap((id) => errors?.[id] ?? [])];

  return (
    <IconContainer>
      <TableStatusIcon
        recordErrors={flattenErrorTypeArray(errors[id]) ?? []}
        recordWarnings={flattenErrorTypeArray(warnings[id]) ?? []}
        errors={flattenErrorTypeArray(applicantEmploymentHistoryErrors)}
        showDefault={!id}
      />
    </IconContainer>
  );
}

function Period(employmentHistory: EmploymentHistory) {
  const { startDate, endDate, currentlyWorkingInThisRole } = employmentHistory as EmploymentWithIncome;
  const formattedStartDate = startDate
    ? formatDateTime(startDate, DateFormatStringEnum.MMMYYYY)
    : '';
  let formattedEndDate = endDate
    ? formatDateTime(endDate, DateFormatStringEnum.MMMYYYY)
    : '';
  if (currentlyWorkingInThisRole) {
    formattedEndDate = 'Current';
  }
  return [formattedStartDate, '-', formattedEndDate].join(' ');
}

function EmploymentStatus(employmentHistory: EmploymentHistory) {
  const { employmentType, id } = employmentHistory;
  const employmentStatus = EmploymentType?.[employmentType as keyof typeof EmploymentType];

  if (!employmentStatus) return '-';

  return (
    <EmploymentStatusWrapper id={`${id}-employment-status`}>
      <EmploymentStatusText>
        {employmentStatus}
      </EmploymentStatusText>
      <Tooltip
        id={`${id}-employment-status`}
        html={employmentStatus}
        place="top"
      />
    </EmploymentStatusWrapper>
  );
}

function Reference(employmentHistory: EmploymentHistory, jobTitles: StaticDataItem[]) {
  const { employmentType } = employmentHistory;
  switch (employmentType) {
    case 'Employed':
    case 'ConstructionIndustryScheme':
      return jobTitles.find(({ key }) => key === employmentHistory.jobTitleCode)?.label || '-';
    case 'DirectorOfLimitedCompany':
    case 'LimitedLiabilityPartnership':
    case 'Partnership':
      return employmentHistory.companyName || '-';
    case 'SoleTrader':
      return employmentHistory.tradingName || '-';
    case 'FosterCarer':
      return employmentHistory.agencyName || '-';
    case 'NotWorkingNotRetired':
      return NotWorkingStatus[employmentHistory.status] || '-';
    default:
      return '-';
  }
}

function DeletePositionButton(employmentHistory: EmploymentHistory, applicantIndex: number) {
  const deleteAlert = useDeleteAlert();
  const {
    deleteFromAccountArray,
  } = useAccountContext();

  const deletePosition = (employmentHistory: EmploymentHistory) => {
    const { id } = employmentHistory;
    deleteAlert({
      onConfirm: () => deleteFromAccountArray(`applicants[${applicantIndex}].incomeAndEmployment.employmentHistory`, id),
      text: 'Are you sure you want to delete this position?',
    });
  };

  return (
    <IconButton onClick={() => deletePosition(employmentHistory)}>
      <DeleteIconButton />
    </IconButton>
  );
}

export default function EmploymentHistorySection({
  applicant,
  applicantIndex,
  ukCountries,
  accountancyQualifications,
  industries,
  jobTitles,
}: {
  applicant: Applicant,
  applicantIndex: number,
  ukCountries: StaticDataItem[],
  accountancyQualifications: StaticDataItem[],
  industries: StaticDataItem[],
  jobTitles: StaticDataItem[],
}) {
  const {
    appendToAccountArray,
    account,
  } = useAccountContext();

  const { errors, warnings } = account ?? {};

  const { incomeAndEmployment } = applicant ?? {};
  const { employmentHistory } = incomeAndEmployment ?? {};

  const employmentHistoryErrors = errorsToPropertyErrors(errors?.[incomeAndEmployment?.id])?.employmentHistory ?? [];
  const addPositionInProgress = useProgressStatusByInitiator('addPosition-button');

  const addPosition = () => {
    appendToAccountArray(`applicants[${applicantIndex}].incomeAndEmployment.employmentHistory`, {}, 'addPosition-button');
  };

  const conditionalRowStyles = useErrorBasedRowStyles(errors, warnings);

  const positionDateErrors = employmentHistory?.map(
    (employmentHistory) => (errors?.[employmentHistory?.id] ?? [])
      .filter((error) => ['startDate', 'endDate'].includes(error.propertyName)),
  )?.flat() ?? [];

  const positionDates = employmentHistory?.reduce((acc, history) => {
    if (history?.startDate || history?.endDate) {
      acc.push(history?.startDate || '', history?.endDate || '');
    }
    return acc;
  }, [] as string[]) ?? [];

  useToast(
    !employmentHistoryErrors.length && !positionDateErrors.length && !!positionDates.length,
    '12 months of employment history have been successfully completed.',
    [employmentHistoryErrors.length, positionDateErrors.length, positionDates.join(',')],
  );

  const columns = [
    {
      name: 'Status',
      cell: (employmentHistory: EmploymentHistory) => StatusIcon(employmentHistory),
      width: '54px',
    },
    {
      name: 'Period',
      cell: (employmentHistory: EmploymentHistory) => Period(employmentHistory),
      width: '160px',
    },
    {
      name: 'Status',
      cell: (employmentHistory: EmploymentHistory) => EmploymentStatus(employmentHistory),
      maxWidth: '220px',
    },
    {
      name: 'Reference',
      selector: (employmentHistory: EmploymentHistory) => Reference(employmentHistory, jobTitles),
    },
    {
      name: '',
      cell: (employmentHistory: EmploymentHistory) => DeletePositionButton(employmentHistory, applicantIndex),
      width: '30px',
    },
  ];

  const sortedEmploymentHistory = employmentHistory
    ?.filter((item) => !!item?.id)
    ?.sort((a, b) => a.ordinal - b.ordinal);

  const employmentHistoryIndices = useMemo(() => employmentHistory.reduce((prev, current, index) => ({
    ...prev,
    [current.id]: index,
  }), {} as Record<string, number>), [employmentHistory]);

  return (
    <Section
      id={applicant?.incomeAndEmployment?.id}
      propertyName="employmentHistory"
      title="Employment history"
      level={2}
      headerCss={ineSectionHeaderCss}
      titleCss={ineSectionTitleCss}
      bodyCss={ineSectionBodyCss}
      errorContainerCss={errorContainerCss}
      errors={errors}
      warnings={warnings}
      titleIcon={<></>}
    >
      <SectionHeader>
        <AddPositionContainer>
          <AddPositionButton
            disabled={addPositionInProgress}
            small
            onClick={addPosition}
          >
            <PlusIcon />
            Add position
          </AddPositionButton>
        </AddPositionContainer>
      </SectionHeader>

      <Table
        columns={columns}
        data={sortedEmploymentHistory ?? []}
        showSearch={false}
        expandableRowsComponent={EmploymentHistoryExpandable}
        expandableRowsComponentProps={{
          applicantIndex,
          ukCountries,
          accountancyQualifications,
          industries,
          jobTitles,
          errors,
          warnings,
          employmentHistoryIndices,
        }}
        conditionalRowStyles={conditionalRowStyles}
      />
    </Section>
  );
}
